import { Component, inject } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  FormControl,
  FormGroup,
  Validators,
  AbstractControl,
} from '@angular/forms';

// Importaciones de entornos.
import { environment } from '../../../environments/environment';

// Importaciones de servicios.
import { LogService } from '../../services/log.service';
import { ProductService } from '../../services/product.service';

// Importaciones de interfaces.
import { IProduct } from '../../interfaces/product.interface';

// Importaciones de modelos.
import { Log } from '../../models/log.model';
import { Product } from '../../models/product.model';

/**
 * Componente de diálogo para la creación del producto.
 *
 * Este componente proporciona una interfaz para crear un producto.
 * Se muestra como un cuadro de diálogo y permite la edición de los datos.
 */
@Component({
  selector: 'app-product-create-dialog',
  templateUrl: './product-create-dialog.component.html',
  standalone: false,
})
export class ProductCreateDialogComponent {
  /**
   * Referencia al diálogo actual.
   *
   * Se utiliza para controlar y cerrar el cuadro de diálogo de la creación del producto.
   */
  private readonly dialogRef: MatDialogRef<ProductCreateDialogComponent> =
    inject(MatDialogRef<ProductCreateDialogComponent>);

  /**
   * Archivo de imagen seleccionado por el usuario.
   *
   * Esta propiedad almacena el archivo de imagen que el usuario selecciona mediante un campo de entrada de tipo `file`.
   * El valor puede ser un objeto de tipo `File` o `null` si no se ha seleccionado ninguna imagen.
   */
  selectedImage: File | null = null;

  /**
   * Vista previa de la imagen seleccionada.
   *
   * Esta propiedad almacena la vista previa de la imagen seleccionada, que puede ser una cadena de texto
   * (base64) o un objeto `ArrayBuffer` generado por el `FileReader`. Si no se ha seleccionado ninguna imagen
   * o no se ha generado la vista previa, el valor será `null`.
   */
  previewImage: string | ArrayBuffer | null = null;

  /**
   * Indica si la operación de creado está en curso.
   *
   * Se utiliza para deshabilitar la interfaz y mostrar un indicador de carga mientras se crea el producto.
   */
  loading: boolean = false;

  /**
   * Almacena el mensaje de error en caso de que ocurra un problema al cambiar la imagen.
   *
   * Puede ser un mensaje de texto o null si no hay error.
   */
  errorOnChangeImage: string | null = null;

  /**
   * Lista de errores.
   *
   * Contiene los mensajes de error generados si la operación de creación falla.
   */
  errorsOnCreate: string[] = [];

  /**
   * Formulario de actualización del producto.
   *
   * Gestiona los campos y validaciones necesarias para crear el producto.
   * Se inicializa como un `FormGroup` y contiene los controles correspondientes a los datos editables.
   */
  form: FormGroup = new FormGroup({
    name: new FormControl('', Validators.required),
    description: new FormControl(),
    status: new FormControl(true, Validators.required),
  });

  /**
   * Obtiene el control del formulario para el campo 'name'.
   *
   * Retorna el control asociado a la propiedad 'name' del formulario,
   * que gestiona el nombre del producto.
   *
   * @returns El control de formulario para 'name'.
   */
  get formName(): AbstractControl | null {
    return this.form.get('name');
  }

  /**
   * Obtiene el control del formulario para el campo 'description'.
   *
   * Retorna el control asociado a la propiedad 'description' del formulario,
   * que gestiona el tipo del producto.
   *
   * @returns El control de formulario para 'description'.
   */
  get formDescription(): AbstractControl | null {
    return this.form.get('description');
  }

  /**
   * Obtiene el control del formulario correspondiente al campo 'status'.
   *
   * Este control indica si el empleado está deshabilitado o no.
   *
   * @returns El control del formulario asociado al campo 'status', o null si no está disponible.
   */
  get formStatus(): AbstractControl | null {
    return this.form.get('status');
  }

  /**
   * Constructor del componente.
   *
   * Este constructor inyecta las dependencias necesarias para el funcionamiento del componente.
   *
   * @param logService - Servicio para gestionar los registros.
   * @param productService - Servicio para gestionar la información del producto.
   * @param snackBar - Servicio para mostrar notificaciones de tipo snack bar.
   */
  constructor(
    private logService: LogService,
    private productService: ProductService,
    private snackBar: MatSnackBar
  ) {}

  /**
   * Maneja el cambio de imagen seleccionada en el input de tipo file.
   * Realiza las validaciones necesarias (tipo y tamaño del archivo) y
   * genera la previsualización de la imagen seleccionada.
   *
   * @param event - Evento de cambio del input de tipo file.
   */
  onChangeImage(event: Event): void {
    const { files } = event.target as HTMLInputElement;
    this.errorOnChangeImage = null;
    this.previewImage = null;
    this.selectedImage = null;

    if (!files?.length) return;

    const file = files[0];
    const types = ['image/png', 'image/jpeg'];
    const maxSize = 2 * 1024 * 1024;

    // Validación de tipo.
    if (!types.includes(file.type)) {
      this.errorOnChangeImage = 'Sólo se permiten archivos PNG, JPG o JPEG.';
      return;
    }

    // Validación de tamaño.
    if (file.size > maxSize) {
      this.errorOnChangeImage = 'El tamaño máximo permitido es de 2MB.';
      return;
    }

    // Leer el archivo como Fecha URL (previsualización).
    const reader = new FileReader();

    reader.onload = () => {
      this.previewImage = reader.result;
      this.selectedImage = file;
    };

    reader.onerror = () => {
      this.errorOnChangeImage = 'No se puede leer el archivo correctamente.';
    };

    reader.readAsDataURL(file);
  }

  /**
   * Crear el producto.
   *
   * Este método se encarga de realizar el proceso de creado del producto.
   * Puede incluir validaciones, llamadas a servicios externos y manejo de errores.
   */
  async create(): Promise<void> {
    this.loading = true;
    this.errorsOnCreate = [];

    try {
      const productResponse: IProduct = await this.productService.create(
        new Product({
          name: this.formName?.value,
          description: this.formDescription?.value,
          imageFile: this.selectedImage,
          disabled: Number(!this.formStatus?.value),
        })
      );

      // Añadir un registro de creación del producto.
      try {
        await this.logService.create(
          new Log({
            description: `Ha creado el producto '${productResponse.id}'.`,
          })
        );
      } catch (errors: any) {
        if (!environment.production) {
          console.warn(
            'No se ha podido guardar el registro de creación del producto.'
          );
        }
      }

      this.snackBar.open(
        `Has creado correctamente el producto ${productResponse.id}`,
        '',
        {
          duration: 5000,
        }
      );

      this.dialogRef.close(true);
    } catch (errors: any) {
      this.errorsOnCreate = errors;
    }

    this.loading = false;
  }
}
